// eslint-disable-next-line no-restricted-imports -- presence overlays wrap the raw MUI overlays
import MuiDialog, { DialogProps, dialogClasses } from "@mui/material/Dialog";
// eslint-disable-next-line no-restricted-imports -- presence overlays wrap the raw MUI overlays
import MuiDrawer, { DrawerProps, drawerClasses } from "@mui/material/Drawer";
import type { PaperProps } from "@mui/material/Paper";
// eslint-disable-next-line no-restricted-imports -- presence overlays wrap the raw MUI overlays
import MuiSnackbar, {
	SnackbarProps,
	snackbarClasses,
} from "@mui/material/Snackbar";
// eslint-disable-next-line no-restricted-imports -- presence overlays wrap the raw MUI overlays
import MuiSwipeableDrawer, {
	SwipeableDrawerProps,
} from "@mui/material/SwipeableDrawer";
import { useForkRef } from "@mui/material/utils";
import { forwardRef } from "react";
import type {
	GhostDialogProps,
	GhostDrawerProps,
	GhostPaperProps,
	GhostSnackbarProps,
} from "./overlay-ghost-store";
import { useOverlayPresence } from "./use-overlay-presence";

const DIALOG_PAPER_SELECTOR = `.${dialogClasses.paper}`;
const DRAWER_PAPER_SELECTOR = `.${drawerClasses.paper}`;
/** The snackbar's content root (its child, or the default `SnackbarContent`). */
const SNACKBAR_CONTENT_SELECTOR = `:scope.${snackbarClasses.root} > *`;

const pickGhostPaperProps = (
	paperProps?: Partial<PaperProps>,
): GhostPaperProps | undefined =>
	paperProps && {
		className: paperProps.className,
		elevation: paperProps.elevation,
		square: paperProps.square,
		style: paperProps.style,
		sx: paperProps.sx,
		variant: paperProps.variant,
	};

const pickGhostDialogProps = (props: DialogProps): GhostDialogProps => ({
	className: props.className,
	fullScreen: props.fullScreen,
	fullWidth: props.fullWidth,
	hideBackdrop: props.hideBackdrop,
	maxWidth: props.maxWidth,
	scroll: props.scroll,
	sx: props.sx,
	TransitionComponent: props.TransitionComponent,
	transitionDuration: props.transitionDuration,
	PaperProps: pickGhostPaperProps(props.PaperProps),
});

const pickGhostDrawerProps = (
	props: DrawerProps | SwipeableDrawerProps,
): GhostDrawerProps => ({
	anchor: props.anchor,
	className: props.className,
	elevation: props.elevation,
	hideBackdrop: props.hideBackdrop ?? props.ModalProps?.hideBackdrop,
	sx: props.sx,
	transitionDuration: props.transitionDuration,
	PaperProps: pickGhostPaperProps(props.PaperProps),
});

const pickGhostSnackbarProps = (props: SnackbarProps): GhostSnackbarProps => ({
	anchorOrigin: props.anchorOrigin,
	className: props.className,
	sx: props.sx,
	TransitionComponent: props.TransitionComponent,
	transitionDuration: props.transitionDuration,
});

/**
 * Drop-in `@mui/material/Dialog` that replays its exit transition when it is
 * unmounted while open (conditional rendering). Requires `OverlayGhostHost`.
 */
export const PresenceDialog = forwardRef<HTMLDivElement, DialogProps>(
	function PresenceDialog(props, ref) {
		const presenceRef = useOverlayPresence(props.open, DIALOG_PAPER_SELECTOR, {
			kind: "dialog",
			props: pickGhostDialogProps(props),
		});
		const handleRef = useForkRef(ref, presenceRef);

		return (
			<MuiDialog
				{...props}
				ref={handleRef}
			/>
		);
	},
);

/**
 * Drop-in `@mui/material/Drawer` that also slides in when mounted already open
 * and replays its exit transition when unmounted while open.
 */
export const PresenceDrawer = forwardRef<HTMLDivElement, DrawerProps>(
	function PresenceDrawer(props, ref) {
		const presenceRef = useOverlayPresence(
			!!props.open,
			DRAWER_PAPER_SELECTOR,
			{ kind: "drawer", props: pickGhostDrawerProps(props) },
		);
		const handleRef = useForkRef(ref, presenceRef);

		return (
			<MuiDrawer
				{...props}
				SlideProps={{ appear: true, ...props.SlideProps }}
				ref={handleRef}
			/>
		);
	},
);

/**
 * Drop-in `@mui/material/SwipeableDrawer` with the same presence behaviour as `PresenceDrawer`.
 */
export const PresenceSwipeableDrawer = forwardRef<
	HTMLDivElement,
	SwipeableDrawerProps
>(function PresenceSwipeableDrawer(props, ref) {
	const presenceRef = useOverlayPresence(!!props.open, DRAWER_PAPER_SELECTOR, {
		kind: "drawer",
		props: pickGhostDrawerProps(props),
	});
	const handleRef = useForkRef(ref, presenceRef);

	return (
		<MuiSwipeableDrawer
			{...props}
			SlideProps={{ appear: true, ...props.SlideProps }}
			ref={handleRef}
		/>
	);
});

/**
 * Drop-in `@mui/material/Snackbar` that replays its exit transition when it is
 * unmounted while open (conditional rendering). MUI already animates its entry.
 */
export const PresenceSnackbar = forwardRef<HTMLDivElement, SnackbarProps>(
	function PresenceSnackbar(props, ref) {
		const presenceRef = useOverlayPresence(
			!!props.open,
			SNACKBAR_CONTENT_SELECTOR,
			{ kind: "snackbar", props: pickGhostSnackbarProps(props) },
		);
		const handleRef = useForkRef(ref, presenceRef);

		return (
			<MuiSnackbar
				{...props}
				ref={handleRef}
			/>
		);
	},
);
